/**
 * Time helpers for a moment that was written down.
 *
 * `moodLabel` and `moodColor` used to live here too, and that was the bug this
 * file now carries a note about rather than the code: this module is reachable
 * from every screen HE opens and from none of the pages he SHARES. So the
 * check-in screen has shown him the word under his thumb since August and a
 * recipient has only ever been given the integer. They are in @adhd/shared now,
 * imported by both sides — see packages/shared/src/mood.ts.
 */

/** The clock time of a moment, in the reader's own locale and zone. */
/** `timeZone` for a page read elsewhere than where it happened — see writtenLate. */
export function formatTime(iso: string, timeZone?: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', timeZone });
}

/**
 * Was this written on a different day from the one it belongs to?
 *
 * True for anything filed under a day other than the one it was written on: a
 * journal written at 3:12am about the Saturday that had just ended, or a
 * check-in that inherited the day of the entry it was made from. Both facts are
 * stored — `day` and `recorded_at` — and showing only the clock time under a
 * Saturday heading reads as Saturday 3am, which is the wrong day and the wrong
 * feeling about the day.
 *
 * `timeZone` is for the shared page, which is read somewhere else by
 * definition: the owner's app can rely on the browser's zone, a clinician's
 * browser cannot. It was the one place this was not used, and it printed
 * "Sep 8, 1:05 AM" — the filed day glued to the clock time — for a check-in
 * made at 1:05 on Sep 9. A moment that never happened, and a newest-first list
 * that then looked out of order.
 */
export function writtenLate(day: string, recordedAt: string, timeZone?: string): boolean {
  return new Date(recordedAt).toLocaleDateString('en-CA', { timeZone }) !== day;
}

/** "Sunday at 3:12 AM" — the weekday, because the point is that it is another day. */
export function whenWritten(recordedAt: string, timeZone?: string, day?: string): string {
  const d = new Date(recordedAt);
  // Written days later ("Write about this day", 14 Oct 2026): a weekday alone
  // would not say which one, so the date does.
  if (day) {
    const recordedDay = d.toLocaleDateString('en-CA', { timeZone });
    if (Math.abs(Date.parse(`${recordedDay}T12:00:00`) - Date.parse(`${day}T12:00:00`)) > 86400000) {
      return `written ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone })}`;
    }
  }
  return `${d.toLocaleDateString(undefined, { weekday: 'long', timeZone })} at ${formatTime(recordedAt, timeZone)}`;
}
